import React, { useState, useEffect, useCallback } from "react";
import { DatePicker, Button, Tooltip } from "antd";
import { ReloadOutlined, RocketOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import styled from "styled-components";

const { RangePicker } = DatePicker;

// Platform launched on Feb 5, 2026 — used as the default start and for the
// "since launch" quick preset.
const PLATFORM_START_DATE = "2026-02-05";
const DATE_STORAGE_KEY = "platformAnalytics:dates";

const loadStoredDates = () => {
  try {
    const raw = localStorage.getItem(DATE_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.startDate && parsed?.endDate) {
        const start = dayjs(parsed.startDate);
        const end = dayjs(parsed.endDate);
        if (start.isValid() && end.isValid()) return [start, end];
      }
    }
  } catch {
    // fall through to default
  }
  return [dayjs(PLATFORM_START_DATE), dayjs()];
};

const PageWrapper = styled.div`
  animation: fadeIn 0.3s ease;
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 28px;
  flex-wrap: wrap;
`;

const PageTitle = styled.h1`
  font-size: 26px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
  letter-spacing: -0.5px;
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

function AnalyticsPageWrapper({ title, subtitle, Component, noDateRange }) {
  const [dates, setDates] = useState(() => loadStoredDates());
  // Bumping refreshKey forces the Component to remount → re-fetches with current dates.
  const [refreshKey, setRefreshKey] = useState(0);

  const startDate = dates[0].format("YYYY-MM-DD");
  const endDate = dates[1].format("YYYY-MM-DD");

  // Persist to localStorage so date selection survives a browser refresh.
  useEffect(() => {
    if (noDateRange) return;
    try {
      localStorage.setItem(
        DATE_STORAGE_KEY,
        JSON.stringify({ startDate, endDate })
      );
    } catch {
      // localStorage unavailable (private mode) — ignore
    }
  }, [startDate, endDate, noDateRange]);

  const setSinceLaunch = useCallback(() => {
    setDates([dayjs(PLATFORM_START_DATE), dayjs()]);
  }, []);

  const handleRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  return (
    <PageWrapper>
      <PageHeader>
        <div>
          <PageTitle>{title}</PageTitle>
          {subtitle && <PageSubtitle>{subtitle}</PageSubtitle>}
        </div>
        {!noDateRange && (
          <Controls>
            <Tooltip title={`Quick preset: from ${PLATFORM_START_DATE} to today`}>
              <Button icon={<RocketOutlined />} onClick={setSinceLaunch}>
                Since Launch (Feb 5 → Today)
              </Button>
            </Tooltip>
            <RangePicker
              value={dates}
              onChange={(v) => {
                if (v) setDates(v);
              }}
              allowClear={false}
              style={{ width: 280 }}
            />
            <Tooltip title="Refresh stats for the selected date range">
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                type="primary"
              />
            </Tooltip>
          </Controls>
        )}
      </PageHeader>
      <Component key={refreshKey} startDate={startDate} endDate={endDate} />
    </PageWrapper>
  );
}

export default AnalyticsPageWrapper;
